<script>
import { notify } from "@kyvg/vue3-notification";
import CustomCheckbox from "@/components/CustomCheckbox";
import submitService from "@/mixins/submitService";

export default {
    name: "ServicePricing",
    components: { CustomCheckbox },
    mixins: [submitService],
    props: {
        fromEditPricing: {
            type: Boolean,
            default: false,
        },
        servicePricing: {
            type: Object,
            default: () => {
            },
        },
        sessionsAdded: {
            type: Boolean,
            default: false,
        },
        isFree: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {

            activityServiceId: 0, // set on mounted
            venueId: 0,
            prices: [
                {
                    price: "",
                    type: "",
                    isDefault: true,
                },
            ],

            priceErrors: [{ price: "", type: "" }],
            freeService: false,
            showPricesTypeDropdown: [],
            existingPriceTypes: ["Per hour", "Per day", "Per term"],
            newPriceType: "",
            toastIdentifer: "service",
        };
    },
    methods: {
        prevStep() {
            this.$router.push({ name: "Service availability" });
        },

        async nextStep() {
            const redirectLink = `${window.location.protocol}//${window.location.host}/management-panel/services`;
            if (this.validate()) {
                this.store();
                const res = await this.submitService();

                if (res.success) {
                    notify({
                        id: this.toastIdentifer,
                        title: "Success",
                        text: "Service listing saved",
                        duration: 3500,
                        type: "success",
                    });
                    this.$storage.clearStorageSync();
                    if (res.data.venueId) {
                        window.onbeforeunload = null;
                        window.location.href = `${redirectLink}/${res.data.venueId}`;
                    }
                }
            }
        },

        validate() {
            const validPrices = this.prices.filter((price, idx) => {
                if (price.price === "") {
                    this.priceErrors[idx].price = "Please add price";
                }

                if (price.price !== "" && ["0", "0.00", "0.0", "00.00", "00.0"].includes(price.price)) {
                    this.priceErrors[idx].price = "Please enter a price or select the ‘free’ checkbox";
                }

                if (price.type === "") {
                    this.priceErrors[idx].type = "Please add type";
                }
                return price.price !== "" && price.type !== "";
            });

            return (validPrices.length === this.prices.length) || this.freeService;
        },

        store() {
            this.$storage.setStorageSync("freeService", this.freeService);
            this.$storage.setStorageSync("prices", this.prices);
        },

        onlyNumber($event) {
            const keyCode = ($event.keyCode ? $event.keyCode : $event.which);
            if ((keyCode < 48 || keyCode > 57) && keyCode !== 46) { // 46 is dot
                $event.preventDefault();
            }
        },

        showPriceType(idx) {
            this.showPricesTypeDropdown[idx] = !this.showPricesTypeDropdown[idx];
        },

        onTypeEnter(idx) {
            if (this.newPriceType !== "") {
                this.prices[idx].type = this.newPriceType;
            }

            this.newPriceType = "";
            this.showPricesTypeDropdown[idx] = false;
        },

        setPriceType(idx, type) {
            this.prices[idx].type = type;

            this.showPricesTypeDropdown[idx] = false;
        },

        toggleDefaultPricing(idx) {
            this.prices = this.prices.map((el) => {
                el.isDefault = false;
                return el;
            });

            this.prices[idx].isDefault = true;
        },

        removePrice(idx) {
            let setNewDefaultPrice = false;
            if (this.prices[idx].isDefault) {
                setNewDefaultPrice = true;
            }
            this.prices.splice(idx, 1);

            this.priceErrors.splice(idx, 1);

            // push empty price object if we removed last price element from the array
            if (this.prices.length === 0) {
                this.prices.push({
                    price: "",
                    type: "",
                    isDefault: true,
                });

                this.priceErrors.push({
                    price: "",
                    type: "",
                });
            }

            if (setNewDefaultPrice && this.prices.length > 0) {
                this.prices[idx].isDefault = true;
            }
        },

        addPricing() {
            this.prices.push({
                price: "",
                type: "",
                isDefault: false,
            });

            this.priceErrors.push({ price: "", type: "" });
        },

        getPricingWrapper() {
            return {
                id: this.activityServiceId,
                venue_id: this.venueId,
                freeService: this.freeService,
                prices: this.prices,
            };
        },

    },

    watch: {
        freeService: {
            handler() {
                this.$emit("updatePricing", this.getPricingWrapper());
            },
        },
        prices: {
            deep: true,
            handler() {
                this.$emit("updatePricing", this.getPricingWrapper());
            },
        },
    },

    computed: {
        showIsFreeCheckbox() {
            if (this.$route.matched[0].name === "Service pricing") {
                const sessions = this.$storage.getStorageSync("checkedWeekday");

                const selectedSession = sessions.filter((session) => session.checked);

                return selectedSession.length > 0;
            }

            return this.sessionsAdded;
        },
    },
    mounted() {
        if (this.fromEditPricing === true) {
            this.activityServiceId = this.servicePricing.id;
            this.venueId = this.servicePricing.venue_id;

            for (let i = 0; i < this.servicePricing.prices.length; i += 1) {
                this.priceErrors.push({ price: "", type: "" });
            }

            if (this.servicePricing.prices.length > 0) {
                this.prices = Object.keys(this.servicePricing.prices).map((idx) => {
                    const price = this.servicePricing.prices[idx];
                    return { ...price, isDefault: price.is_default === "1" };
                });
            }

            this.freeService = this.isFree;
        } else {
            const prices = this.$storage.getStorageSync("prices") || [];

            if (prices.length > 0) {
                this.prices = prices;
            }

            this.freeService = this.$storage.getStorageSync("freeService") || false;
        }
    },
};
</script>

<template>
    <div class="pricing mx-auto" id="service-pricing">
        <div class="mt-4 mb-5 service-steps" v-if="!fromEditPricing">
            Step 5 of 5
        </div>

        <h2 class="mb-5" data-test-id="test-service-pricing-header">
            <span v-if="fromEditPricing">Edit </span>Pricing
        </h2>

        <CustomCheckbox
            v-if="showIsFreeCheckbox"
            @checkbox-click="freeService = !freeService"
            :checked="freeService"
        >
            <div class="checkbox-main-text">My service is free</div>
        </CustomCheckbox>

        <div v-if="!freeService" class="">
            <div class="mt-3 d-flex flex-column">
                <div
                    :key="pIdx"
                    v-for="(price, pIdx) in prices"
                    class="d-flex flex-row mt-2">
                    <div class="position-relative w-25 mr-3">
                        <input @keypress="onlyNumber" class="pl-4 price w-100" type="text" v-model="price.price"
                               placeholder="Price"/>
                        <span class="ticket-symbol">£</span>
                        <div v-if="priceErrors[pIdx]?.price" class="error-text">
                            {{ priceErrors[pIdx]?.price }}
                        </div>
                    </div>
                    <div class="w-25">
                        <input @click="showPriceType(pIdx)" :value="price.type" readonly class="ticket-type-input w-100"
                               type="text"
                               placeholder="Price type"/>
                        <div v-show="showPricesTypeDropdown[pIdx]" class="add-ticket-type-container">
                            <div class="tickets-title mb-2">Choose from an existing price type</div>
                            <div @click="setPriceType(pIdx, priceType)" v-for="priceType in existingPriceTypes"
                                 class="ticket-type cursor-pointer"
                                 :class="{'selected': prices[pIdx]?.type === priceType}">
                                {{ priceType }}
                            </div>
                            <div class="d-flex flex-column">
                                <input @keyup.enter="onTypeEnter(pIdx)" v-model="newPriceType" class="mt-3" type="text"
                                       id="service-price-new-type"
                                       placeholder="Price type"/>
                            </div>
                        </div>

                        <div v-if="priceErrors[pIdx]?.type" class="error-text">
                            {{ priceErrors[pIdx]?.type }}
                        </div>
                    </div>

                    <div
                        v-if="pIdx !== 0
                            || (pIdx === 0 && prices[0].price !== '' || prices[0].type !== '')"
                        @click="removePrice(pIdx)"
                        class="ml-3 align-self-center action edit cursor-pointer"
                    >
                        Remove
                    </div>
                    <div
                        v-if="pIdx !== 0
                            || (pIdx === 0 && prices[0].price !== '' || prices[0].type !== '')"
                        @click="toggleDefaultPricing(pIdx)"
                        class="ml-3 align-self-center cursor-pointer"
                        :class="{'text-decoration-underline': price.isDefault}"
                    >
                        {{ price.isDefault ? "Default pricing" : "Set as default" }}
                    </div>
                </div>

                <button
                    @click="addPricing"
                    v-if="prices[0].price !== '' && prices[0].type !== ''"
                    class="btn-new btn-new-outline ml-0 mr-auto mt-3 add-price">
                    + Add another price
                </button>
            </div>

        </div>

        <div class="mt-5 d-flex flex-row" v-if="!fromEditPricing">
            <button @click="prevStep" class="ml-0 px-3 py-2 d-block btn-new btn-new-outline font-weight-bold">
                Back
            </button>
            <button @click="nextStep" type="submit" class="ml-auto px-4 py-3 d-block btn-new font-weight-bold">Finish
            </button>
        </div>
        <ToastNotification :identifer="toastIdentifer"/>
    </div>
</template>

<style scoped>
.add-price {
    padding: 1rem 1.5rem;
}

#service-price-new-type {
    width: 90% !important;
}
</style>
